import { FC } from 'react';

import { Box, BoxProps, GlobeOutline, IconProps, OfficeBuildingOutline, rem, UserGroupOutline } from '@bonusly/design-system';

import { NomineeGroupType } from '../../../schemas/nominations';

const ICON_BY_GROUP_TYPE: Record<NomineeGroupType, FC<IconProps>> = {
	department: OfficeBuildingOutline,
	location: GlobeOutline,
	team: UserGroupOutline,
};

const BG_COLOR_BY_GROUP_TYPE: Record<NomineeGroupType, string> = {
	department: 'purpleAlpha.250',
	location: 'greenAlpha.200',
	team: 'blueAlpha.200',
};

const ICON_COLOR_BY_GROUP_TYPE: Record<NomineeGroupType, string> = {
	department: 'purple.400',
	location: 'green.400',
	team: 'blue.400',
};

// Box dimensions chosen to mirror the design-system Avatar sizes so a `GroupAvatar`
// can drop into a layout that previously rendered an Avatar without disturbing the
// surrounding spacing. Icon sizes are picked at ~50% of the box dimension to match
// the look of the Add a Feed modal's filter rows.
const SIZE_TOKENS = {
	sm: { box: 32, icon: 'xs' as const },
	md: { box: 48, icon: 'md' as const },
};

type GroupAvatarSize = keyof typeof SIZE_TOKENS;

type GroupAvatarProps = Omit<BoxProps, 'size'> & {
	groupType: NomineeGroupType;
	size?: GroupAvatarSize;
};

// Circular icon-as-avatar for group nominees (department / location / team). Mirrors
// the visual treatment from the Add a Feed modal's filter rows so the same group
// concept reads consistently across admin and recipient surfaces.
export const GroupAvatar: FC<GroupAvatarProps> = ({ groupType, size = 'sm', ...rest }) => {
	const Icon = ICON_BY_GROUP_TYPE[groupType];
	const { box, icon } = SIZE_TOKENS[size];

	return (
		<Box
			w={rem(box)}
			h={rem(box)}
			minW={rem(box)}
			bgColor={BG_COLOR_BY_GROUP_TYPE[groupType]}
			borderRadius="full"
			display="flex"
			alignItems="center"
			justifyContent="center"
			color={ICON_COLOR_BY_GROUP_TYPE[groupType]}
			{...rest}
		>
			<Icon size={icon} />
		</Box>
	);
};
